/**
 * Filter drawer for the API Credentials view.
 * Only contains the Organizations filter (SUPER_ADMIN only).
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { FilterDrawer } from "../../components/filters/FilterDrawer";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../components/filters/filterStyles";

const filterFormId = "api-credentials-filter-form";

type ApiCredentialsFilterProps = {
  filters: SearchInput<GraphQL.ApiCredentialSearchFields>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.ApiCredentialSearchFields>[]) => void;
};

export function ApiCredentialsFilter({
  isOpened,
  filters,
  onClose,
  onApply,
}: { isOpened: boolean } & ApiCredentialsFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} onClose={onClose}>
      <ApiCredentialsFilterContent filters={filters} onClose={onClose} onApply={onApply} />
    </FilterDrawer>
  );
}

function ApiCredentialsFilterContent({ filters: initialFilters, onClose, onApply }: ApiCredentialsFilterProps) {
  const { classes } = useFilterStyles();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.ApiCredentialSearchFields.CustomerPublicId, initialFilters),
    },
  });

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.ApiCredentialSearchFields.CustomerPublicId, customerPublicId),
    ]);
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        <OrganizationFilter {...getInputProps("selectedCustomer")} />
      </form>
    </Stack>
  );
}
